import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, Check } from 'lucide-react';
import { Input } from '@/components/ui/input';

const ease: [number, number, number, number] = [0.22, 1, 0.36, 1];

const subjects = [
  { name: 'Mathematics Methods 4', area: 'Mathematics' },
  { name: 'General Mathematics 3', area: 'Mathematics' },
  { name: 'Biology 3', area: 'Science' },
  { name: 'Chemistry 3', area: 'Science' },
  { name: 'Physical Sciences 3', area: 'Science' },
  { name: 'Psychology 3', area: 'Science' },
  { name: 'English 3', area: 'English' },
  { name: 'English Studio 3', area: 'English' },
  { name: 'Modern History 3', area: 'Humanities' },
  { name: 'Legal Studies 3', area: 'Humanities' },
  { name: 'Accounting 3', area: 'Business' },
  { name: 'Digital Technologies 3', area: 'Technologies' },
];

export default function SubjectExplorer() {
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState(subjects[0].name);
  const filtered = subjects.filter((s) => s.name.toLowerCase().includes(query.toLowerCase()));
  const current = subjects.find((s) => s.name === selected);

  return (
    <section className="relative py-16 md:py-20" aria-labelledby="subject-explorer-heading">
      <div className="container mx-auto px-6 md:px-8 lg:px-12 max-w-5xl">
        <p className="text-sm font-semibold tracking-[0.2em] uppercase text-accent mb-3 text-center">Your subjects</p>
        <h2 id="subject-explorer-heading" className="font-display text-3xl md:text-4xl font-bold text-center mb-8">
          Explore selected TCE subjects
        </h2>
        <p className="text-sm text-muted-foreground text-center mb-6">A preview of our curated catalogue — not every TASC course is supported.</p>

        <div className="relative max-w-md mx-auto mb-6">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search TCE subjects…"
            aria-label="Search TCE subjects"
            className="pl-10 rounded-full h-12"
          />
        </div>

        <div className="flex flex-wrap justify-center gap-2 mb-8">
          {filtered.map((s) => (
            <button
              key={s.name}
              onClick={() => setSelected(s.name)}
              className={`min-h-[44px] rounded-full px-4 text-sm border transition-all duration-300 ${
                selected === s.name
                  ? 'bg-accent text-accent-foreground border-accent shadow-md shadow-accent/20'
                  : 'border-border/60 text-muted-foreground hover:text-foreground hover:border-accent/50'
              }`}
            >
              {s.name}
            </button>
          ))}
          {filtered.length === 0 && (
            <p className="text-sm text-muted-foreground">No match in this preview. Browse available subjects after signing in, or request a subject from Courses.</p>
          )}
        </div>

        <AnimatePresence mode="wait">
          {current && (
            <motion.div
              key={current.name}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.4, ease }}
              className="glass-card rounded-2xl border border-border/50 bg-card/70 backdrop-blur-xl p-6 max-w-2xl mx-auto"
            >
              <p className="text-xs uppercase tracking-[0.15em] text-muted-foreground mb-1">{current.area}</p>
              <h3 className="font-display text-xl font-semibold mb-4">{current.name}</h3>
              <ul className="grid sm:grid-cols-2 gap-3 text-sm">
                {['Mapped to the official TASC course', 'Marked against A–D grade standards', 'Practice questions for every skill', 'Study plan built around your exam date'].map((f) => (
                  <li key={f} className="flex gap-2 items-start">
                    <Check className="w-4 h-4 text-accent mt-0.5 shrink-0" />
                    <span className="text-foreground/90">{f}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
}
